import { theme } from '@maestro/local/theme'

const DEFAULT_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
}

export function formatDate(
  date: Date,
  localeOverride?: string,
  options: Intl.DateTimeFormatOptions = DEFAULT_DATE_FORMAT
): string {
  const dateLocale = localeOverride || theme().site.locale
  return new Intl.DateTimeFormat(dateLocale, options).format(date)
}

/** `yyyy-MM-dd` in the local time zone (the buyer's, in the browser), as date inputs expect. */
export function getLocalIsoDate(date: Date = new Date()): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${date.getFullYear()}-${month}-${day}`
}

export function formatOrdinalDate(date: Date, localeOverride?: string): string {
  const EN_ORDINAL_SUFFIXES: Record<Intl.LDMLPluralRule, string> = {
    zero: 'th',
    one: 'st',
    two: 'nd',
    few: 'rd',
    many: 'th',
    other: 'th',
  }

  function hasOrdinalSuffixes(locale: string): boolean {
    return locale.startsWith('en')
  }

  const locale = localeOverride || theme().site.locale

  if (!hasOrdinalSuffixes(locale)) {
    return formatDate(date, locale)
  }

  const parts = new Intl.DateTimeFormat(
    locale,
    DEFAULT_DATE_FORMAT
  ).formatToParts(date)

  const day = Number(parts.find((part) => part.type === 'day')?.value)
  const suffix =
    EN_ORDINAL_SUFFIXES[
      new Intl.PluralRules(locale, { type: 'ordinal' }).select(day)
    ]

  return parts
    .map((part) => {
      if (part.type === 'day') {
        return `${day}${suffix}`
      }
      if (part.type === 'literal') {
        return part.value.replace(',', '')
      }
      return part.value
    })
    .join('')
}

/**
 * Calculates the calendar-day difference between a target date and today.
 * Returns 0 for today, a positive number for future dates, and a negative number for past dates.
 */
export function daysUntil(validFrom: string): number {
  const targetDate = new Date(validFrom)
  const today = new Date()

  // Normalize both dates to midnight to avoid timezone/time-of-day issues
  targetDate.setHours(0, 0, 0, 0)
  today.setHours(0, 0, 0, 0)

  const diffMs = targetDate.getTime() - today.getTime()

  return Math.round(diffMs / (1000 * 60 * 60 * 24))
}

export function isSameDay(compareDate: Date, date: Date = new Date()): boolean {
  const { locale, timeZone } = theme().site
  const formatOptions: Intl.DateTimeFormatOptions = {
    timeZone,
    dateStyle: 'short',
  }
  return (
    formatDate(date, locale, formatOptions) ===
    formatDate(compareDate, locale, formatOptions)
  )
}

export function isSameDayClient(
  date: Date,
  clientDate: Date = new Date()
): boolean {
  const { locale, timeZone } = theme().site
  return (
    formatDate(date, locale, {
      timeZone,
      dateStyle: 'short',
    }) === formatDate(clientDate, locale, { dateStyle: 'short' })
  )
}

export function getWeekdayIndex(date: Date = new Date()): number {
  const { timeZone } = theme().site
  const formatOptions: Intl.DateTimeFormatOptions = {
    timeZone,
    weekday: 'short',
  }
  const weekday = formatDate(date, 'en-US', formatOptions)
  const map: Record<string, number> = {
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
    Sun: 7,
  }
  return map[weekday]
}

export function getWeekdayIndexClient(date: Date = new Date()): number {
  const index = date.getDay()
  return index === 0 ? 7 : index
}

export function currentTimeInTimeZone(): string {
  const { timeZone } = theme().site
  const formatOptions: Intl.DateTimeFormatOptions = {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }
  return formatDate(new Date(), 'en-US', formatOptions)
}
